import { atom, useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";

/**
 * Name of the CSS `@keyframes` in `index.css` that `.animate-thread-flash`
 * runs. The row handlers compare `event.animationName` against this so an
 * unrelated animation ending on the row doesn't clear the flag early; keeping
 * it here (rather than a bare string in each handler) means a rename only
 * happens in one place.
 */
export const THREAD_FLASH_ANIMATION = "thread-flash";

/**
 * Thread ids that should briefly "flash" wherever they appear in a thread list.
 * Used to draw the eye to a row that changed while the user's focus was
 * elsewhere — today that's "send in background": a freshly created thread lands
 * in the left aside while the user stays put, so we pulse its row to show where
 * it went.
 *
 * A flagged thread usually isn't in the list yet — it only arrives once the
 * socket invalidates and the list refetches. So an id stays flagged until the
 * row actually mounts and finishes its one-shot animation (it clears itself via
 * `clearFlash`), with `FLASH_SAFETY_MS` as a backstop in case the row never
 * appears (panel closed, refetch never lands) so a stale flag can't fire on
 * some unrelated later remount.
 */
export const flashThreadIdsAtom = atom<number[]>([]);

/** Backstop for a flag whose row never mounts. Generous on purpose. */
const FLASH_SAFETY_MS = 12000;

/**
 * Drop `threadId` from the flag list, returning the SAME array when it isn't
 * present. The happy-path `clearFlash` (on animationEnd) and the backstop
 * timeout both call this, so the second one to run must be a no-op reference-
 * wise — otherwise it churns a new array and re-renders every subscribed row.
 */
const withoutId = (prev: number[], threadId: number): number[] =>
  prev.includes(threadId) ? prev.filter((id) => id !== threadId) : prev;

export interface ThreadFlash {
  /** True while this thread should render its one-shot flash animation. */
  flash: boolean;
  /** Call when the animation ends so the flag doesn't linger / re-fire. */
  clearFlash: () => void;
}

export const useThreadFlash = (threadId: number): ThreadFlash => {
  const flash = useAtomValue(flashThreadIdsAtom).includes(threadId);
  const setFlashIds = useSetAtom(flashThreadIdsAtom);
  const clearFlash = useCallback(
    () => setFlashIds((prev) => withoutId(prev, threadId)),
    [setFlashIds, threadId],
  );
  return { flash, clearFlash };
};

/**
 * Returns a callback that flags `threadId` to flash once in any thread list. The
 * row clears the flag itself on animation end; the timeout is only a backstop
 * (and a no-op once the row has already cleared it).
 */
export const useTriggerThreadFlash = (): ((threadId: number) => void) => {
  const setFlashIds = useSetAtom(flashThreadIdsAtom);
  return useCallback(
    (threadId: number) => {
      setFlashIds((prev) => (prev.includes(threadId) ? prev : [...prev, threadId]));
      setTimeout(() => {
        setFlashIds((prev) => withoutId(prev, threadId));
      }, FLASH_SAFETY_MS);
    },
    [setFlashIds],
  );
};
